import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { clinicLogo, useClinicBranding } from '../context/ClinicBrandingContext';
import { resolveMediaUrl } from '../lib/api';
import { toast } from 'sonner';
import { Lock, Mail } from 'lucide-react';
import { isStaffRole } from '../lib/adminRoles';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_b4b171bf-a957-41aa-a103-fc3ac15c898d/artifacts/jwcnyg2y_logo_Podologo.png";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { login, user, loading: authLoading } = useAuth();
  const { info: brandingInfo } = useClinicBranding();
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const logoUrl = resolveMediaUrl(clinicLogo(brandingInfo) || LOGO_URL);
  const brandName = String(brandingInfo?.name || '').trim() || 'Pizzas';

  useEffect(() => {
    if (authLoading) return;
    if (isStaffRole(user?.role)) {
      navigate('/admin', { replace: true });
    }
  }, [authLoading, user, navigate]);

  if (authLoading || isStaffRole(user?.role)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="admin-login-redirect">
        <div className="animate-pulse text-muted-foreground">Cargando panel…</div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(loginId, password);
      if (userData?.mfa_required || userData?.totp_setup_required) {
        toast.error('Usa /super/login para el acceso de plataforma.');
        return;
      }
      if (!isStaffRole(userData.role)) {
        toast.error('Acceso denegado. Solo administradores.');
        return;
      }
      toast.success('¡Bienvenido!');
      navigate('/admin');
    } catch (error) {
      const message = error.response?.data?.detail || 'Credenciales inválidas';
      toast.error(typeof message === 'string' ? message : 'Error de autenticación');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="admin-login-page">
      <Card className="w-full max-w-md border border-border">
        <CardHeader className="text-center pb-2">
          <img src={logoUrl} alt="Logo" className="h-16 w-auto mx-auto mb-4 object-contain" />
          <CardTitle className="font-heading text-2xl">{brandName}</CardTitle>
          <p className="text-muted-foreground text-sm mt-1">Panel de administración</p>
          <p className="text-muted-foreground text-sm">Ingresa tus credenciales</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="loginId" className="flex items-center gap-1 mb-1">
                <Mail className="w-3 h-3" /> Usuario o correo electrónico
              </Label>
              <Input
                id="loginId"
                type="text"
                autoComplete="username"
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
                required
                placeholder="usuario o admin@tu-negocio.com"
                className="h-11"
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="flex items-center gap-1 mb-1">
                <Lock className="w-3 h-3" /> Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="h-11"
                data-testid="login-password"
              />
            </div>
            <Button
              type="submit"
              className="w-full rounded-full"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Ingresando...' : 'Ingresar'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
